import { useEffect, useState } from 'react';
import { Control, Controller, FieldValues, Path, useController } from 'react-hook-form';
import { StyleSheet, View } from 'react-native';
import { Checkbox } from 'react-native-paper';

import { useColor } from '@/hooks/useColor';
import { i18n } from '@/utils/i18n';

import ListAccordion from '../ListAccordion';
import Text from '../Text';
import { OptionType } from './types';

export type CheckboxAccordionGroupProps<T extends FieldValues> = {
  control: Control<T>;
  name: Path<T>;
  options: OptionType[];
  groupLabel?: string;
  description?: string;
  showError?: boolean;
  disabled?: boolean;
};

const CheckboxAccordionGroup = <T extends FieldValues>({
  name,
  control,
  options,
  groupLabel,
  description,
  showError = true,
  disabled
}: CheckboxAccordionGroupProps<T>) => {
  const defaultNames: string[] = [];
  const colors = useColor();

  const [checkedValues, setCheckedValues] = useState<string[]>(defaultNames);

  const handleSelect = (checkedName: string) => {
    const newValues = checkedValues.includes(checkedName)
      ? checkedValues?.filter((name) => name !== checkedName)
      : [...(checkedValues ?? []), checkedName];
    setCheckedValues(newValues);
    return newValues;
  };

  const {
    field: { value },
    fieldState: { error, isTouched }
  } = useController({ name, control });

  useEffect(() => {
    if (!isTouched && value) {
      setCheckedValues(value);
    }
  }, [isTouched, value]);

  return (
    <View>
      <View>
        <ListAccordion title={groupLabel} description={description} disabled={disabled}>
          {options.length === 0 ? (
            <Text>{i18n.t('mix.no_options')}</Text>
          ) : (
            options.map((option, index) => (
              <View style={styles.mainContainer} key={`checkbox-label-${name}-${option?.label}-${index}`}>
                <Controller
                  name={name}
                  control={control}
                  render={({ field: { onChange } }) => (
                    <Checkbox.Android
                      key={`checkbox-${name}-${option?.label}-${index}`}
                      status={checkedValues.includes(option?.value) ? 'checked' : 'unchecked'}
                      onPress={() => onChange(handleSelect(option?.value))}
                      color={colors.primary}
                    />
                  )}
                />
                <Text style={styles.labelText}>{option?.label}</Text>
              </View>
            ))
          )}
        </ListAccordion>
      </View>
      {showError && <Text style={styles.errorText}>{!!error?.message ? error.message : ' '}</Text>}
    </View>
  );
};

const styles = StyleSheet.create({
  mainContainer: {
    flexDirection: 'row',
    alignItems: 'center'
  },

  groupLabel: {
    fontSize: 18,
    marginBottom: 18,
    color: 'black'
  },

  labelText: {
    fontSize: 14,
    color: 'black',
    fontWeight: '400'
  },

  errorText: {
    marginTop: 10,
    fontSize: 12,
    color: 'red'
  }
});

export default CheckboxAccordionGroup;
